import * as R from 'ramda'
import { useMemo } from 'react'

import {
  deleteChildElementDeepMutator,
  deleteChildNodesDeepMutator,
} from 'components/Editor/EditorContext/mutators/block'
import { getDeepCloneNode } from 'components/Editor/EditorContext/selectors/block'
import { createColNode } from 'components/Editor/EditorContext/struct/block'
import type { IFieldConfig } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { isArrayMeta, isArrayValues } from 'components/uiKit/ArrayControl'
import type { Block } from 'services/Store/Project/types'

export const useThroughElementItemsArrayEffect = (_config: IFieldConfig<Block>) => {
  const block = useFormData<Block>()
  return useMemo(
    () =>
      ({
        effect: (fieldChange) => {
          const { value, name, meta } = fieldChange
          if (!isArrayValues(value) || !isArrayMeta(meta)) {
            return fieldChange
          }
          const nameArr = name?.split('.') || []

          if (meta.command === 'add') {
            const createNodes = []
            const createElements = []
            const nodeId = meta.item.value
            if (value.length > 1) {
              const { nodes, elements, id } = getDeepCloneNode(block, value[0].value)
              createNodes.push(
                ...nodes.map((node) => (node.id === id ? { ...node, id: nodeId } : node)),
              )
              createElements.push(...elements)
            } else {
              const column = createColNode()
              column.id = nodeId
              createNodes.push(column)
            }
            const newBlock = R.compose(
              R.assocPath(nameArr, value),
              //@ts-expect-error R.compose retype needed
              ...createNodes.map((node) => R.assocPath(['schema', 'nodes', node.id], node)),
              ...createElements.map((element) => R.assocPath(['elements', element.id], element)),
            )(block)

            return { value: newBlock }
          }

          if (meta.item && meta.clone && meta.command === 'clone') {
            const createNodes = []
            const createElements = []
            const nodeId = meta.clone.value
            const { nodes, elements, id } = getDeepCloneNode(block, meta.item.value)
            createNodes.push(
              ...nodes.map((node) => (node.id === id ? { ...node, id: nodeId } : node)),
            )
            createElements.push(...elements)

            const newBlock = R.compose(
              R.assocPath(nameArr, value),
              //@ts-expect-error R.compose retype needed
              ...createNodes.map((node) => R.assocPath(['schema', 'nodes', node.id], node)),
              ...createElements.map((element) => R.assocPath(['elements', element.id], element)),
            )(block)

            return { value: newBlock }
          }

          if (meta.item && meta.command === 'delete') {
            const newBlock = R.compose(
              R.assocPath(nameArr, value),
              deleteChildNodesDeepMutator(block, meta.item.value),
              deleteChildElementDeepMutator(block, meta.item.value),
            )(block)
            return { value: newBlock }
          }

          return fieldChange
        },
      }) satisfies Partial<IFieldConfig<Block>>,
    [block],
  )
}
